import * as React from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";

import { cn } from "@/utils/utils";
import { isEmpty } from "lodash";

/** Where the medium and high colour bands start; below `medium` is low. */
export interface ProgressLevels {
  medium: number;
  high: number;
}

interface ProgressProps
  extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  value: number;
  levels?: ProgressLevels;
  staticColor?: string;
  indicatorClassname?: string;
}

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(
  (
    {
      className,
      value,
      levels = { medium: 33, high: 66 },
      staticColor,
      indicatorClassname,
      ...props
    },
    ref
  ) => {
    const getIndicatorColor = () => {
      if (value == null || value < levels.medium) {
        return { className: "bg-charts-negative" };
      }
      if (!isEmpty(staticColor)) {
        return { style: { backgroundColor: staticColor } };
      }
      if (value < levels.high) {
        return { className: "bg-charts-warning" };
      }
      return { className: "bg-charts-positive" };
    };

    return (
      <ProgressPrimitive.Root
        ref={ref}
        className={cn(
          "relative h-1 w-full overflow-hidden rounded-full bg-charts-grey-strong",
          className
        )}
        {...props}
      >
        <ProgressPrimitive.Indicator
          className={cn(
            "h-1 w-full flex-1 border-r border-border-widget-graph transition-all",
            getIndicatorColor().className,
            indicatorClassname
          )}
          style={{
            transform: `translateX(-${100 - (value || 0)}%)`,
            ...(getIndicatorColor().style || {})
          }}
        />
      </ProgressPrimitive.Root>
    );
  }
);
Progress.displayName = ProgressPrimitive.Root.displayName;

export { Progress };
